import React, { useState, useEffect, useRef, useMemo } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { AnimatePresence, motion } from "framer-motion";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import {
  MagnifyingGlass, ArrowsDownUp, DotsThreeVertical, PencilSimple, Trash,
  EnvelopeSimple, Phone, WhatsappLogo, X, Receipt, Plus, PaperPlaneTilt,
} from "@phosphor-icons/react";
import {
  PageHeader, Input, Select, Button, Badge, Skeleton, EmptyState, Pagination,
  Table, THead, TBody, TR, TH, TD, cn,
} from "../../components/ui";
import EditOrderSidebar from "./EditOrderSidebar";
import IssueQuoteModal from "./IssueQuoteModal";
import Devis from "./Devis";
import { quoteTotals, formatMAD, lineNet } from "./quote";

const FRONTEND = process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";

const SourceTag = ({ source }) =>
  source === "internal" ? <Badge tone="accent">Internal</Badge> : <Badge tone="neutral">Website</Badge>;

/* ---------- helpers ---------- */
const AVATAR_COLORS = ["#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];
const colorFor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const initials = (first, last) => {
  const f = String(first || "");
  const l = String(last || "");
  return `${f[0] || ""}${l[0] || ""}`.toUpperCase() || "?";
};

const Avatar = ({ first, last, size = 36 }) => (
  <span
    className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
    style={{ backgroundColor: colorFor(`${first || ""}${last || ""}`), width: size, height: size, fontSize: size * 0.4 }}
  >
    {initials(first, last)}
  </span>
);

const lineTotal = (p) => Number(p?.pivot?.price ?? p?.price ?? 0) * Number(p?.pivot?.quantity ?? 0);
const orderTotal = (order) => (order?.products || []).reduce((sum, p) => sum + lineTotal(p), 0);
const shortDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const longDate = (d) =>
  new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const STATUS = {
  pending: { label: "New", tone: "warning", active: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  quoted: { label: "Quote sent", tone: "accent", active: "border-orange-500/40 bg-orange-500/10 text-orange-600 dark:text-orange-400" },
  approved: { label: "Approved", tone: "success", active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  rejected: { label: "Rejected", tone: "danger", active: "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" },
  expired: { label: "Expired", tone: "neutral", active: "border-line bg-surface-2 text-ink" },
  processing: { label: "In progress", tone: "info", active: "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  completed: { label: "Completed", tone: "success", active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  cancelled: { label: "Cancelled", tone: "danger", active: "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" },
};
// Statuses the admin sets by hand. quoted/approved/rejected are driven by the
// quotation flow (Send quotation + the customer's online decision).
const PIPELINE = ["pending", "processing", "completed", "cancelled"];
const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, tone: "neutral" };
  return <Badge tone={s.tone} dot>{s.label}</Badge>;
};

const TABS = [
  { value: "all", label: "All" },
  { value: "pending", label: "New" },
  { value: "quoted", label: "Quote sent" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "processing", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

/* ---------- row action menu ---------- */
const RowMenu = ({ order, onEdit, onDelete, canUpdate, canDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-pop" onClick={(e) => e.stopPropagation()}>
          <Devis
            orderData={order}
            label="Download devis"
            iconSize={15}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink transition hover:bg-surface-2"
          />
          {canUpdate && (
            <button onClick={() => { setOpen(false); onEdit(order); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink transition hover:bg-surface-2">
              <PencilSimple size={15} /> Edit request
            </button>
          )}
          {canDelete && (
            <button onClick={() => { setOpen(false); onDelete(order); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 transition hover:bg-red-500/10">
              <Trash size={15} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- detail (docked panel + drawer content) ---------- */
const QuoteDetail = ({ order, onClose, onEdit, onStatus, onSendQuote, sending, canUpdate, showClose }) => {
  if (!order) {
    return (
      <div className="relative flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-line bg-surface p-8 text-center">
        {showClose && (
          <button
            onClick={onClose}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        )}
        <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl border border-line bg-surface-2 text-faint">
          <Receipt size={22} />
        </div>
        <p className="text-sm font-medium text-ink">No request selected</p>
        <p className="mt-1 text-sm text-muted">Pick a request from the list to review it.</p>
      </div>
    );
  }

  const items = order.products || [];
  const name = `${order.first_name || ""} ${order.last_name || ""}`.trim() || "—";
  const wa = (order.phone || "").replace(/\D/g, "");

  const ContactBtn = ({ href, icon: Icon, label, ext }) => (
    <a
      href={href}
      {...(ext ? { target: "_blank", rel: "noreferrer" } : {})}
      className="flex flex-col items-center gap-1 rounded-xl border border-line bg-surface-2 py-2.5 text-muted transition hover:border-line-strong hover:text-ink"
    >
      <Icon size={17} />
      <span className="text-[11px] font-medium">{label}</span>
    </a>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-surface">
      {/* header */}
      <div className="flex items-start justify-between border-b border-line px-5 py-4">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted">Request</div>
          <h2 className="truncate text-base font-semibold text-ink">#{order.order_number}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <StatusBadge status={order.status} />
            <SourceTag source={order.source} />
            <span className="text-xs text-faint">{longDate(order.created_at)}</span>
          </div>
        </div>
        {showClose && (
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
            <X size={17} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* customer */}
        <div className="border-b border-line px-5 py-4">
          <div className="flex items-center gap-3">
            <Avatar first={order.first_name} last={order.last_name} size={44} />
            <div className="min-w-0">
              <div className="truncate font-semibold text-ink">{name}</div>
              {order.company_name ? <div className="truncate text-xs text-muted">{order.company_name}</div> : null}
              {order.city ? <div className="truncate text-xs text-muted">{order.city}</div> : null}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {order.email && <ContactBtn href={`mailto:${order.email}`} icon={EnvelopeSimple} label="Email" />}
            {order.phone && <ContactBtn href={`tel:${order.phone}`} icon={Phone} label="Call" />}
            {wa && <ContactBtn href={`https://wa.me/${wa}`} icon={WhatsappLogo} label="WhatsApp" ext />}
          </div>
        </div>

        {/* status pipeline */}
        {canUpdate && (
          <div className="border-b border-line px-5 py-4">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted">Update status</div>
            <div className="grid grid-cols-2 gap-2">
              {PIPELINE.map((key) => {
                const s = STATUS[key];
                return (
                  <button
                    key={key}
                    onClick={() => onStatus(order, key)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-medium transition",
                      order.status === key ? s.active : "border-line text-muted hover:bg-surface-2 hover:text-ink"
                    )}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* requested items */}
        <div className="px-5 py-4">
          <div className="mb-3 text-sm font-semibold text-ink">Requested items</div>
          {items.length === 0 ? (
            <p className="text-sm text-faint">No items on this request.</p>
          ) : (
            <ul className="space-y-3">
              {items.map((p) => (
                <li key={p.id} className="flex items-center gap-3">
                  <img src={p.images?.[0] || "/placeholder.svg"} alt="" className="h-11 w-11 rounded-lg border border-line bg-surface-2 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-ink">{p.name}</p>
                    <p className="text-xs text-muted">
                      Qty: {p.pivot?.quantity || 0}
                      {Number(p.pivot?.discount) > 0
                        ? ` · −${p.pivot.discount}${(p.pivot.discount_type || "percent") === "percent" ? "%" : " MAD"}`
                        : ""}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-ink">{formatMAD(lineNet(p))}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {order.comments ? (
          <div className="border-t border-line px-5 py-4">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Customer note</div>
            <p className="text-sm text-ink">{order.comments}</p>
          </div>
        ) : null}
      </div>

      {/* footer */}
      <div className="border-t border-line px-5 py-4">
        {(() => {
          const { subtotal, globalDiscount, total, vatRate, vatAmount, totalTTC } = quoteTotals(order);
          return (
            <div className="mb-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">Subtotal</span>
                <span className="text-ink">{formatMAD(subtotal)}</span>
              </div>
              {globalDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted">Remise</span>
                  <span className="text-ink">- {formatMAD(globalDiscount)}</span>
                </div>
              )}
              {vatRate > 0 ? (
                <>
                  <div className="flex justify-between"><span className="text-muted">Total HT</span><span className="text-ink">{formatMAD(total)}</span></div>
                  <div className="flex justify-between"><span className="text-muted">TVA ({vatRate}%)</span><span className="text-ink">{formatMAD(vatAmount)}</span></div>
                  <div className="flex items-center justify-between pt-1"><span className="font-medium text-ink">Total TTC</span><span className="text-lg font-semibold text-ink">{formatMAD(totalTTC)}</span></div>
                </>
              ) : (
                <div className="flex items-center justify-between pt-1">
                  <span className="font-medium text-ink">Estimated total</span>
                  <span className="text-lg font-semibold text-ink">{formatMAD(total)}</span>
                </div>
              )}
            </div>
          );
        })()}
        {order.quote_token ? (
          <div className="mb-3 rounded-lg border border-line bg-surface-2 p-3">
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted">Customer quote link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={`${FRONTEND}/devis/${order.quote_token}`} className="min-w-0 flex-1 truncate rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink outline-none" />
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(`${FRONTEND}/devis/${order.quote_token}`); toast.success("Link copied"); }}
                className="shrink-0 rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-ink transition hover:bg-surface"
              >
                Copy
              </button>
            </div>
          </div>
        ) : null}
        {canUpdate && (
          <button
            onClick={() => onSendQuote(order)}
            disabled={sending}
            className="mb-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-accent text-sm font-medium text-accent-fg transition hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {sending ? "Issuing…" : ["quoted", "approved", "rejected", "expired"].includes(order.status) ? "Re-issue quotation" : "Issue quotation"}
          </button>
        )}
        <div className="flex items-center gap-2">
          <Devis
            orderData={order}
            label="Download devis"
            iconSize={16}
            className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-line text-sm font-medium text-ink transition hover:bg-surface-2"
          />
          {canUpdate && (
            <Button variant="outline" icon={PencilSimple} onClick={() => onEdit(order)}>
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- page ---------- */
function Orders() {
  const { checkAccess } = useAccessChecker();
  const canCreate = checkAccess("orders", "create");
  const canUpdate = checkAccess("orders", "update");
  const canDelete = checkAccess("orders", "delete");

  const [searchValue, setSearchValue] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  const [deleteModal, setDeleteModal] = useState(false);
  const [OrderId, setOrderId] = useState(null);
  const [done, setDone] = useState(false);

  const [editSidebarOpen, setEditSidebarOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [panelOrder, setPanelOrder] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(true);

  const debounceRef = useRef(null);

  useEffect(() => {
    getOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => getOrders(1), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const getOrders = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/orders/search?page=${page}&per_page=10`, { searchValue: searchValue || "" });
      const data = response.data;
      if (data) {
        setOrders(data.data);
        setCurrentPage(data.current_page);
        setTotalPages(data.last_page);
        setPanelOrder((prev) => (prev ? data.data?.find((o) => o.id === prev.id) || prev : data.data?.[0] || null));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const visibleOrders = useMemo(() => {
    let list = [...orders];
    if (statusFilter !== "all") list = list.filter((o) => (o.status || "").toLowerCase() === statusFilter);
    if (sortBy === "date") list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortBy === "total") list.sort((a, b) => orderTotal(b) - orderTotal(a));
    else if (sortBy === "name") list.sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""));
    return list;
  }, [orders, statusFilter, sortBy]);

  const selectOrder = (order) => {
    setPanelOrder(order);
    setDetailOpen(true);
    setDrawerOpen(true);
  };
  const openCreate = () => {
    setSelectedOrder(null);
    setEditSidebarOpen(true);
  };
  const handleEdit = (order) => {
    setSelectedOrder(order);
    setEditSidebarOpen(true);
  };
  const handleDelete = (order) => {
    setOrderId(order.id);
    setDeleteModal(true);
  };
  const handleSaved = (saved) => {
    setDone((d) => !d);
    if (saved && panelOrder?.id === saved.id) setPanelOrder(saved);
  };

  const [quoteModalOrder, setQuoteModalOrder] = useState(null);
  const handleIssued = (updated) => {
    if (!updated) return;
    setOrders((list) => list.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    setPanelOrder((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
  };

  const updateStatus = async (order, status) => {
    if (order.status === status) return;
    const prev = order.status;
    const apply = (st) => {
      setOrders((list) => list.map((o) => (o.id === order.id ? { ...o, status: st } : o)));
      setPanelOrder((p) => (p && p.id === order.id ? { ...p, status: st } : p));
    };
    apply(status);
    try {
      await axiosInstance.put(`/orders/${order.id}/status`, { status });
    } catch (e) {
      toast.error("Couldn't update status");
      apply(prev);
    }
  };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Quote requests"
        subtitle="Customer devis requests — review, prepare quotes and track status."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search requests…"
              containerClassName="w-full sm:w-60"
            />
            <Select
              className="w-44"
              icon={ArrowsDownUp}
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: "date", label: "Newest first" },
                { value: "total", label: "Highest value" },
                { value: "name", label: "Customer A–Z" },
              ]}
            />
            {canCreate && (
              <Button icon={Plus} onClick={openCreate}>
                New quote
              </Button>
            )}
          </>
        }
      />

      {/* status tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-1 rounded-xl border border-line bg-surface p-1">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setStatusFilter(t.value)}
            className={cn(
              "rounded-lg px-3.5 py-1.5 text-sm font-medium transition",
              statusFilter === t.value ? "bg-accent text-accent-fg" : "text-muted hover:bg-surface-2 hover:text-ink"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-5">
        {/* list */}
        <div className="min-w-0 flex-1">
          <Table>
            <THead sticky>
              <TR className="hover:bg-transparent">
                <TH>Request</TH>
                <TH>Customer</TH>
                <TH>Items</TH>
                <TH>Est. value</TH>
                <TH>Status</TH>
                <TH>Date</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {loading &&
                Array.from({ length: 7 }).map((_, i) => (
                  <TR key={i} className="hover:bg-transparent">
                    <TD><Skeleton className="h-3.5 w-20" /></TD>
                    <TD><div className="flex items-center gap-2.5"><Skeleton className="h-8 w-8 rounded-full" /><Skeleton className="h-3.5 w-28" /></div></TD>
                    <TD><Skeleton className="h-3.5 w-8" /></TD>
                    <TD><Skeleton className="h-3.5 w-16" /></TD>
                    <TD><Skeleton className="h-5 w-20 rounded-full" /></TD>
                    <TD><Skeleton className="h-3.5 w-20" /></TD>
                    <TD><Skeleton className="ml-auto h-8 w-8 rounded-lg" /></TD>
                  </TR>
                ))}

              {!loading &&
                visibleOrders.map((order) => {
                  const active = panelOrder?.id === order.id;
                  return (
                    <TR
                      key={order.id}
                      onClick={() => selectOrder(order)}
                      className={cn("cursor-pointer", active && "bg-surface-2 ring-1 ring-inset ring-accent/30")}
                    >
                      <TD>
                        <div className="font-medium text-ink">#{order.order_number}</div>
                        <div className="mt-1"><SourceTag source={order.source} /></div>
                      </TD>
                      <TD>
                        <div className="flex items-center gap-2.5">
                          <Avatar first={order.first_name} last={order.last_name} size={32} />
                          <div className="min-w-0">
                            <div className="truncate text-ink">{order.first_name} {order.last_name}</div>
                            {order.company_name ? <div className="truncate text-xs text-muted">{order.company_name}</div> : null}
                          </div>
                        </div>
                      </TD>
                      <TD className="text-muted">{(order.products || []).length}</TD>
                      <TD className="font-medium text-ink">{formatMAD(quoteTotals(order).total)}</TD>
                      <TD><StatusBadge status={order.status} /></TD>
                      <TD className="text-muted">{shortDate(order.created_at)}</TD>
                      <TD onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                          <RowMenu order={order} onEdit={handleEdit} onDelete={handleDelete} canUpdate={canUpdate} canDelete={canDelete} />
                        </div>
                      </TD>
                    </TR>
                  );
                })}

              {!loading && visibleOrders.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon={Receipt} title="No requests found" description="Quote requests from your store will appear here." />
                  </td>
                </tr>
              )}
            </TBody>
          </Table>

          <Pagination
            className="mt-4"
            page={currentPage}
            totalPages={totalPages}
            onChange={(p) => getOrders(p)}
            summary={`Page ${currentPage} of ${totalPages}`}
          />
        </div>

        {/* docked detail (xl+) */}
        {detailOpen && (
          <div className="hidden w-[380px] shrink-0 xl:block">
            <div className="sticky top-[5.5rem]">
              <QuoteDetail
                order={panelOrder}
                onEdit={handleEdit}
                onStatus={updateStatus}
                onSendQuote={setQuoteModalOrder}
                sending={false}
                canUpdate={canUpdate}
                showClose
                onClose={() => setDetailOpen(false)}
              />
            </div>
          </div>
        )}
      </div>

      {/* reopen affordance when the panel is closed */}
      {!detailOpen && (
        <button
          onClick={() => setDetailOpen(true)}
          className="fixed bottom-6 right-6 z-30 hidden items-center gap-2 rounded-full border border-line bg-surface px-4 py-2.5 text-sm font-medium text-ink shadow-pop transition hover:bg-surface-2 xl:inline-flex"
        >
          <Receipt size={16} />
          Show details
        </button>
      )}

      {/* drawer detail (< xl) */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm xl:hidden"
            onClick={() => setDrawerOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <QuoteDetail
                order={panelOrder}
                onClose={() => setDrawerOpen(false)}
                onEdit={(o) => { setDrawerOpen(false); handleEdit(o); }}
                onStatus={updateStatus}
                onSendQuote={setQuoteModalOrder}
                sending={false}
                canUpdate={canUpdate}
                showClose
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DeleteModal
        setDeleteId={setOrderId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={OrderId}
        Model="orders"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
      <EditOrderSidebar
        isOpen={editSidebarOpen}
        onClose={() => setEditSidebarOpen(false)}
        order={selectedOrder}
        onSaved={handleSaved}
      />
      <IssueQuoteModal
        open={!!quoteModalOrder}
        order={quoteModalOrder}
        onClose={() => setQuoteModalOrder(null)}
        onIssued={handleIssued}
      />
    </div>
  );
}

export default Orders;
